/**
 * Wind sway on the GPU, in two places that both end in `modifySplatCenter`:
 *
 * - Wind-reactive AREAS of the scan run in the splat render stage, composed into the scene-wide
 *   `gsplatModifyVS` by splat-region-effects.ts (its center hook). That chunk runs every frame for
 *   every splat (WebGL2 quad renderer, WebGPU raster and compute projector, and the splat shadow
 *   casters on both backends), so the sway needs no work-buffer re-copy: a frame costs one area
 *   test per splat and the sway for splats inside an area.
 * - Wind-reactive SPLAT OBJECTS (separated or extra splats) run in that object's copy stage, as
 *   its work-buffer modifier (workBufferModifiers.ts composes it with any other modifier on the
 *   same component). Pushing the clock re-copies only that object's splats, and everything that
 *   reads the work buffer (sorting on WebGPU, shadows, picking) sees the moved splats.
 *
 * Both evaluate `windOffset(p, A, B, C, D)` (scene coordinates) from the same library, the GPU twin
 * of windOffsetCPU in windParams.ts. Uniforms: uWindA = (time, strength, gustiness, turbulence),
 * uWindB = (direction x, direction z, speed, gust length); per part A/B/C/D (writeWindPartUniforms).
 */
import { regionUniformShader } from '../region-shape';
import { outlineSlabShader } from '../outline-shape';
import type { ResolvedArea } from '../area-shape';

/** The area shapes wind sways: a sphere, a box or an outline (not other kinds the shared shapes may gain). */
export type WindAreaShape = Extract<ResolvedArea, { kind: 'sphere' | 'box' | 'outline' }>;
export const isWindAreaShape = (area: ResolvedArea): area is WindAreaShape => area.kind === 'sphere' || area.kind === 'box' || area.kind === 'outline';

export const WIND_UNIFORM_A = 'uWindA';
export const WIND_UNIFORM_B = 'uWindB';

const LIBRARY_GLSL = /* glsl */ `
uniform vec4 uWindA;
uniform vec4 uWindB;

float wind_hash(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
}

float wind_noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = p - i;
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(wind_hash(i), wind_hash(i + vec2(1.0, 0.0)), u.x), mix(wind_hash(i + vec2(0.0, 1.0)), wind_hash(i + vec2(1.0, 1.0)), u.x), u.y);
}

vec3 windOffset(vec3 p, vec4 A, vec4 B, vec4 C, vec4 D) {
    float s = uWindA.y;
    if (s <= 0.0 || A.y <= 0.0) return vec3(0.0);
    float t = uWindA.x;
    float g = uWindA.z;
    vec2 dir = uWindB.xy;
    vec2 perp = vec2(-dir.y, dir.x);
    float L = uWindB.w;
    float along = dot(p.xz, dir);
    float across = dot(p.xz, perp);
    float x = along - uWindB.z * t;
    float gust = 0.65 * wind_noise(vec2(x / L, across / (1.6 * L))) + 0.35 * wind_noise(vec2(x / (0.45 * L) + 17.0, across / (0.7 * L) + 17.0));
    vec2 q = vec2(along, across) / (0.6 * L);
    float tu = wind_noise(q + vec2(t * 0.9 + 3.0, -t * 0.6 + 3.0)) - 0.5;
    float tc = wind_noise(q + vec2(-t * 0.7 + 41.0, t * 0.8 + 41.0)) - 0.5;
    float phase = B.w + 6.2832 * wind_noise(p.xz / (8.0 * A.y) + 7.0);
    float w = 6.2832 * B.x * t + phase;
    float load = s * (0.3 + g * (gust - 0.45) * 1.8);
    float osc = s * sin(w) * (0.25 + 0.6 * g * gust);
    float fAlong = load + osc + uWindA.w * s * 1.2 * tu;
    float fCross = s * sin(w * 1.37 + 1.3) * (0.08 + 0.15 * g * gust) + uWindA.w * s * tc;
    // Flutter phase from the position: leaf clusters (about a twentieth of the part's height) move
    // together, with a little per-splat jitter, so a crown rustles instead of boiling.
    float hs = wind_noise(vec2(p.x + p.y * 0.6, p.z - p.y * 0.4) / (0.05 * A.y) + 23.0) * 0.85 + wind_hash(p.xz * (97.0 / A.y) + p.y * (61.0 / A.y)) * 0.15;
    float ft = 6.2832 * t;
    vec3 o;
    float k;
    if (A.w < 0.5) {
        float h = clamp((p.y - A.x) / A.y, 0.0, 1.0);
        k = max(0.0, (h - B.z) / max(1.0 - B.z, 0.05));
        vec2 d = (fAlong * dir + fCross * perp) * (A.z * A.y * k * k);
        float r = max(k * A.y * (1.0 - B.z), 0.05 * A.y);
        o = vec3(d.x, -0.5 * dot(d, d) / r, d.y);
        // Branches: away from the trunk and higher up, bob and sway a few times faster, each
        // branch-sized patch with its own phase.
        float radial = length(p.xz - C.zw) / max(D.x, 0.0001);
        float wb = smoothstep(0.15, 1.0, radial) * k * D.y;
        if (wb > 0.0) {
            float bp = 6.2832 * wind_noise(vec2(p.x + p.y * 0.7, p.z - p.y * 0.4) / (0.3 * A.y) + 11.0);
            float bw = 6.2832 * B.x * 3.2 * t + bp;
            float ba = A.z * A.y * 0.18 * s * (0.35 + 0.65 * gust) * wb;
            float side = sin(bw * 0.83 + 1.7) * 0.5;
            o += vec3(dir.x * side, sin(bw), dir.y * side) * ba;
        }
    } else {
        float u = clamp((along - C.x) / C.y, 0.0, 1.0);
        k = max(0.0, (u - B.z) / max(1.0 - B.z, 0.05));
        float wave = sin(6.2832 * (along / (0.75 * C.y) - B.x * (0.6 + s) * t) + phase);
        float wave2 = cos(6.2832 * (along / (0.5 * C.y) - B.x * 1.3 * (0.6 + s) * t) + phase * 1.7);
        float amp = A.z * C.y * pow(k, 1.3) * s * (0.7 + 0.6 * g * gust);
        vec2 side = perp * (wave * amp * 0.35) - dir * (amp * 0.08);
        o = vec3(side.x, wave2 * amp * 0.15, side.y);
    }
    float fa = B.y * 0.012 * A.y * s * k;
    o += fa * vec3(sin(ft * (2.6 + 1.4 * hs) + 6.2832 * hs), 0.6 * sin(ft * (3.1 + 1.1 * hs) + 2.1 + 6.2832 * hs), cos(ft * (2.3 + 1.7 * hs) + 4.2 + 6.2832 * hs));
    return o;
}
`;

const LIBRARY_WGSL = /* wgsl */ `
uniform uWindA: vec4f;
uniform uWindB: vec4f;

fn wind_hash(p: vec2f) -> f32 {
    var p3 = fract(vec3f(p.x, p.y, p.x) * 0.1031);
    p3 = p3 + dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
}

fn wind_noise(p: vec2f) -> f32 {
    let i = floor(p);
    let f = p - i;
    let u = f * f * (3.0 - 2.0 * f);
    return mix(mix(wind_hash(i), wind_hash(i + vec2f(1.0, 0.0)), u.x), mix(wind_hash(i + vec2f(0.0, 1.0)), wind_hash(i + vec2f(1.0, 1.0)), u.x), u.y);
}

fn windOffset(p: vec3f, A: vec4f, B: vec4f, C: vec4f, D: vec4f) -> vec3f {
    let s = uniform.uWindA.y;
    if (s <= 0.0 || A.y <= 0.0) { return vec3f(0.0); }
    let t = uniform.uWindA.x;
    let g = uniform.uWindA.z;
    let dir = uniform.uWindB.xy;
    let perp = vec2f(-dir.y, dir.x);
    let L = uniform.uWindB.w;
    let along = dot(p.xz, dir);
    let across = dot(p.xz, perp);
    let x = along - uniform.uWindB.z * t;
    let gust = 0.65 * wind_noise(vec2f(x / L, across / (1.6 * L))) + 0.35 * wind_noise(vec2f(x / (0.45 * L) + 17.0, across / (0.7 * L) + 17.0));
    let q = vec2f(along, across) / (0.6 * L);
    let tu = wind_noise(q + vec2f(t * 0.9 + 3.0, -t * 0.6 + 3.0)) - 0.5;
    let tc = wind_noise(q + vec2f(-t * 0.7 + 41.0, t * 0.8 + 41.0)) - 0.5;
    let phase = B.w + 6.2832 * wind_noise(p.xz / (8.0 * A.y) + 7.0);
    let w = 6.2832 * B.x * t + phase;
    let load = s * (0.3 + g * (gust - 0.45) * 1.8);
    let osc = s * sin(w) * (0.25 + 0.6 * g * gust);
    let fAlong = load + osc + uniform.uWindA.w * s * 1.2 * tu;
    let fCross = s * sin(w * 1.37 + 1.3) * (0.08 + 0.15 * g * gust) + uniform.uWindA.w * s * tc;
    let hs = wind_noise(vec2f(p.x + p.y * 0.6, p.z - p.y * 0.4) / (0.05 * A.y) + 23.0) * 0.85 + wind_hash(p.xz * (97.0 / A.y) + p.y * (61.0 / A.y)) * 0.15;
    let ft = 6.2832 * t;
    var o: vec3f;
    var k: f32;
    if (A.w < 0.5) {
        let h = clamp((p.y - A.x) / A.y, 0.0, 1.0);
        k = max(0.0, (h - B.z) / max(1.0 - B.z, 0.05));
        let d = (fAlong * dir + fCross * perp) * (A.z * A.y * k * k);
        let r = max(k * A.y * (1.0 - B.z), 0.05 * A.y);
        o = vec3f(d.x, -0.5 * dot(d, d) / r, d.y);
        let radial = length(p.xz - C.zw) / max(D.x, 0.0001);
        let wb = smoothstep(0.15, 1.0, radial) * k * D.y;
        if (wb > 0.0) {
            let bp = 6.2832 * wind_noise(vec2f(p.x + p.y * 0.7, p.z - p.y * 0.4) / (0.3 * A.y) + 11.0);
            let bw = 6.2832 * B.x * 3.2 * t + bp;
            let ba = A.z * A.y * 0.18 * s * (0.35 + 0.65 * gust) * wb;
            let side = sin(bw * 0.83 + 1.7) * 0.5;
            o = o + vec3f(dir.x * side, sin(bw), dir.y * side) * ba;
        }
    } else {
        let u = clamp((along - C.x) / C.y, 0.0, 1.0);
        k = max(0.0, (u - B.z) / max(1.0 - B.z, 0.05));
        let wave = sin(6.2832 * (along / (0.75 * C.y) - B.x * (0.6 + s) * t) + phase);
        let wave2 = cos(6.2832 * (along / (0.5 * C.y) - B.x * 1.3 * (0.6 + s) * t) + phase * 1.7);
        let amp = A.z * C.y * pow(k, 1.3) * s * (0.7 + 0.6 * g * gust);
        let side = perp * (wave * amp * 0.35) - dir * (amp * 0.08);
        o = vec3f(side.x, wave2 * amp * 0.15, side.y);
    }
    let fa = B.y * 0.012 * A.y * s * k;
    o = o + fa * vec3f(sin(ft * (2.6 + 1.4 * hs) + 6.2832 * hs), 0.6 * sin(ft * (3.1 + 1.1 * hs) + 2.1 + 6.2832 * hs), cos(ft * (2.3 + 1.7 * hs) + 4.2 + 6.2832 * hs));
    return o;
}
`;

export const WIND_LIBRARY_GLSL = LIBRARY_GLSL;
export const WIND_LIBRARY_WGSL = LIBRARY_WGSL;

/** Uniform names of an area slot: its shape (sphere / box: `name` mat4 + `nameP`), and A / B / C / D. */
export const windAreaUniform = (index: number) => `uWindArea${index}`;

/**
 * The render-stage center hook for wind areas: `region_wind_center(sceneCenter, offset)` adds
 * the sway of every area the splat is in (weighted by the area's soft edge, normalised where
 * areas overlap). Sphere / box transforms ride in uniforms; outlines are baked (editing one
 * recompiles once, as for focus and haze).
 */
export function windAreaCenterShader(areas: ReadonlyArray<WindAreaShape>, lang: 'glsl' | 'wgsl'): string {
    const glsl = lang === 'glsl';
    const u = (name: string) => (glsl ? name : `uniform.${name}`);
    const decls = areas
        .map((area, i) => {
            const name = windAreaUniform(i);
            const params = glsl
                ? `uniform vec4 ${name}A;\nuniform vec4 ${name}B;\nuniform vec4 ${name}C;\nuniform vec4 ${name}D;\n`
                : `uniform ${name}A: vec4f;\nuniform ${name}B: vec4f;\nuniform ${name}C: vec4f;\nuniform ${name}D: vec4f;\n`;
            const weight = area.kind === 'outline' ? `${outlineSlabShader(area.slab, `windWeight${i}`, lang)}\n` : regionUniformShader(area.kind, `windWeight${i}`, name, lang);
            return params + weight;
        })
        .join('\n');
    const body = areas
        .map((_, i) => {
            const name = windAreaUniform(i);
            return glsl
                ? `{ float w = windWeight${i}(sceneCenter); if (w > 0.0) { sum += w * windOffset(sceneCenter, ${name}A, ${name}B, ${name}C, ${name}D); total += w; } }`
                : `{ let w = windWeight${i}(sceneCenter); if (w > 0.0) { sum = sum + w * windOffset(sceneCenter, ${u(name)}A, ${u(name)}B, ${u(name)}C, ${u(name)}D); total = total + w; } }`;
        })
        .join('\n    ');
    return glsl
        ? `${LIBRARY_GLSL}
${decls}
void region_wind_center(vec3 sceneCenter, inout vec3 offset) {
    if (uWindA.y <= 0.0) return;
    vec3 sum = vec3(0.0);
    float total = 0.0;
    ${body}
    offset += sum / max(total, 1.0);
}`
        : `${LIBRARY_WGSL}
${decls}
fn region_wind_center(sceneCenter: vec3f, offset: ptr<function, vec3f>) {
    if (uniform.uWindA.y <= 0.0) { return; }
    var sum = vec3f(0.0);
    var total = 0.0;
    ${body}
    *offset = *offset + sum / max(total, 1.0);
}`;
}

/** Per-object uniforms (component parameters): the part, and world <-> scene. */
export const WIND_PART_UNIFORMS = {
    a: 'uWindPartA',
    b: 'uWindPartB',
    c: 'uWindPartC',
    d: 'uWindPartD',
    toScene: 'uWindToScene',
    toWorld: 'uWindToWorld',
} as const;

/**
 * A wind-reactive splat object's work-buffer modifier (copy stage). The copy stage hands
 * modifySplatCenter the world centre; the sway is computed in scene coordinates.
 */
export const WIND_OBJECT_MODIFIER_GLSL = `${LIBRARY_GLSL}
uniform vec4 uWindPartA;
uniform vec4 uWindPartB;
uniform vec4 uWindPartC;
uniform vec4 uWindPartD;
uniform mat4 uWindToScene;
uniform mat4 uWindToWorld;

void modifySplatCenter(inout vec3 center) {
    vec3 p = (uWindToScene * vec4(center, 1.0)).xyz;
    vec3 o = windOffset(p, uWindPartA, uWindPartB, uWindPartC, uWindPartD);
    center += (uWindToWorld * vec4(o, 0.0)).xyz;
}
void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {}
void modifySplatColor(vec3 center, inout vec4 color) {}
`;

export const WIND_OBJECT_MODIFIER_WGSL = `${LIBRARY_WGSL}
uniform uWindPartA: vec4f;
uniform uWindPartB: vec4f;
uniform uWindPartC: vec4f;
uniform uWindPartD: vec4f;
uniform uWindToScene: mat4x4f;
uniform uWindToWorld: mat4x4f;

fn modifySplatCenter(center: ptr<function, vec3f>) {
    let p = (uniform.uWindToScene * vec4f(*center, 1.0)).xyz;
    let o = windOffset(p, uniform.uWindPartA, uniform.uWindPartB, uniform.uWindPartC, uniform.uWindPartD);
    *center = *center + (uniform.uWindToWorld * vec4f(o, 0.0)).xyz;
}
fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {}
fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {}
`;
